import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { type Conversation } from "shared/apiClient";

import { useCouncilLocalisation } from "shared/hooks";
import { useAccount } from "@/hooks";

import {
  BaysideCityCouncilLogo,
  BoroondaraCityCouncilLogo,
  EastGippslandCouncilLogo, MeltonCityCouncilLogo,
  MorningtonPeninsulaShireCouncilLogo,
  MyLotLogoBlue,
  SunshineCoastCouncilLogo,
} from "shared/components";
import { YarraRangesIcon } from "@/components/icons";

const logoMap = {
  BAY: <BaysideCityCouncilLogo className="h-full w-full" />,
  BOR: <BoroondaraCityCouncilLogo className="h-full w-full" />,
  MP: <MorningtonPeninsulaShireCouncilLogo className="h-full w-full" />,
  YRC: <YarraRangesIcon />,
  SSC: (
    <div className="flex h-full w-full items-center justify-center">
      <SunshineCoastCouncilLogo className="h-[130%] w-[130%]" />
    </div>
  ),
  EG: <EastGippslandCouncilLogo className="h-full w-full" />,
  MELT: <MeltonCityCouncilLogo className="h-full w-full" />,
};

function useGetCouncilCode(conversation: Conversation | undefined | null) {
  const [searchParams] = useSearchParams();

  return (
    searchParams.get("cc") ??
    conversation?.council_code ??
    ""
  ).toUpperCase();
}

export interface LocalisedLogoProps {
  useMyLotLogo?: boolean;
}

export function LocalisedLogo({ useMyLotLogo }: LocalisedLogoProps) {
  const { conversation } = useAccount();
  const { getLocalisationEntry } = useCouncilLocalisation(conversation);

  const councilCode = useGetCouncilCode(conversation);

  const logo = useMemo(() => {
    if (useMyLotLogo) {
      return <MyLotLogoBlue />;
    }
    return councilCode in logoMap ? logoMap[councilCode] : <MyLotLogoBlue />;
  }, [councilCode, useMyLotLogo]);

  return (
    <a
      className="flex h-full w-full items-center justify-center"
      href={getLocalisationEntry("councilPlanningWebsite")}
      tabIndex={0}
    >
      {logo}
    </a>
  );
}
